export function tierForQuantity(count) {
  return count >= 6 && count <= 11 ? 10 : count >= 12 && count <= 19 ? 15 : count === 20 ? 18 : 0;
}

export function compareSizeLabels(a, b) {
  const clean = value => value.trim().replace(/^size\s*/i, '');
  const left = clean(a), right = clean(b);
  if (/^0+$/.test(left) && /^0+$/.test(right)) return right.length - left.length;
  if (left !== '' && right !== '' && Number.isFinite(Number(left)) && Number.isFinite(Number(right))) return Number(left) - Number(right);
  return left.localeCompare(right, undefined, { numeric: true });
}

export function projectedSavings(quantity, prices) {
  if (!Number.isInteger(quantity) || quantity < 6 || quantity > 20 || !prices.length) return null;
  const percentage = tierForQuantity(quantity);
  return { percentage, minimum: Math.round(Math.min(...prices) * quantity * percentage / 100), maximum: Math.round(Math.max(...prices) * quantity * percentage / 100) };
}

export function summarizeStack(items, extendPrice, prepPrice) {
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  const percentage = tierForQuantity(count);
  const bagGross = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
  const saving = Math.round(bagGross * percentage / 100);
  return { count, percentage, bagGross, saving, total: extendPrice + prepPrice + bagGross - saving };
}

export function quantityToAdd(requested, currentTotal, currentVariant, stock) {
  if (!Number.isInteger(requested) || requested < 1) return 0;
  if (currentTotal + requested > 20 || currentVariant + requested > stock) return 0;
  return requested;
}

export function countDistinctSizes(items) {
  return new Set(items.map(item => item.title.trim().toLowerCase()).filter(Boolean)).size;
}

export function summarizeEligibleStack(items, extendPrice, prepPrice) {
  const totals = summarizeStack(items, extendPrice, prepPrice);
  const distinct = countDistinctSizes(items);
  const valid = totals.count >= 6 && totals.count <= 20 && distinct >= 6 &&
    items.every(item => Number.isInteger(item.quantity) && item.quantity > 0);
  return {
    ...totals, distinct, valid,
    percentage: valid ? totals.percentage : 0,
    saving: valid ? totals.saving : 0,
    total: valid ? totals.total : extendPrice + prepPrice + totals.bagGross
  };
}

export function selectionMessage(selection, dataset, targetQuantity) {
  const messages = [];
  if (selection.count === 0) messages.push(dataset.messageSelect);
  else if (!selection.valid) messages.push(dataset.messageInvalid);
  if (dataset.requiredReady !== 'true') messages.push(dataset.messageUnavailable);
  if (dataset.orderingEnabled !== 'true') messages.push(dataset.messagePreview);
  if (!messages.length && targetQuantity && selection.count < targetQuantity) {
    messages.push(dataset.messagePresetTarget.replace('__COUNT__', String(targetQuantity)));
  }
  return messages.filter(Boolean).join(' ');
}

export function mountStack(root) {
    if (!root || !root.querySelector('[data-add-stack]')) return;

    const selected = new Map();
    let targetQuantity = 6;
    const distinctSizes = () => countDistinctSizes([...selected.values()]);
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: root.dataset.currency }).format(cents / 100);
    const bagCount = root.querySelector('[data-bag-count]');
    const distinctCount = root.querySelector('[data-distinct-count]');
    const tier = root.querySelector('[data-tier]');
    const selectedList = root.querySelector('[data-selected-bags]');
    const addStack = root.querySelector('[data-add-stack]');
    const status = root.querySelector('[data-status]');
    const replaceCount = (template, count) => template.replace('__COUNT__', String(count));
    const customQuantity = root.querySelector('[data-custom-quantity]');
    const customBox = root.querySelector('[data-custom-box]');
    const prices = [...root.querySelectorAll('[data-bag-variant] option')].map(option => Number(option.dataset.priceCents));
    const savingsLabel = (amount, percentage) => root.dataset.saveTemplate.replace('__AMOUNT__', amount).replace('__PERCENT__', String(percentage));
    const goalLabel = quantity => {
      const estimate = projectedSavings(quantity, prices);
      if (!estimate) return '';
      const amount = estimate.minimum === estimate.maximum ? money(estimate.minimum) : `${money(estimate.minimum)}–${money(estimate.maximum)}`;
      return savingsLabel(amount, estimate.percentage);
    };

    const getTotal = () => [...selected.values()].reduce((total, item) => total + item.quantity, 0);
    const getTier = (count) => {
      if (count >= 6 && count <= 11) return { percentage: 10, label: root.getAttribute('data-tier-10') };
      if (count >= 12 && count <= 19) return { percentage: 15, label: root.getAttribute('data-tier-15') };
      if (count === 20) return { percentage: 18, label: root.getAttribute('data-tier-18') };
      return { percentage: 0, label: '' };
    };

    const render = () => {
      const totals = summarizeEligibleStack([...selected.values()], Number(root.dataset.extendPrice), Number(root.dataset.prepPrice));
      const { count, distinct, valid } = totals;
      root.querySelector('[data-bags-subtotal]').textContent = money(totals.bagGross);
      root.querySelector('[data-bags-saving]').textContent = money(totals.saving);
      root.querySelector('[data-stack-total]').textContent = money(totals.total);
      const currentSaving = totals.percentage ? savingsLabel(money(totals.saving), totals.percentage) : root.dataset.messageSelect;
      root.querySelector('[data-checkout-savings]').textContent = currentSaving;
      root.querySelector('[data-current-savings]').textContent = totals.percentage ? root.dataset.selectionSavingsTemplate.replace('__SAVING__', currentSaving) : currentSaving;
      root.querySelector('[data-goal-savings]').textContent = targetQuantity ? replaceCount(root.dataset.goalSavingsTemplate, targetQuantity).replace('__SAVING__', goalLabel(targetQuantity)) : '';
      const currentTier = getTier(count);
      bagCount.textContent = replaceCount(root.dataset.bagCountTemplate, count);
      distinctCount.textContent = replaceCount(root.dataset.distinctCountTemplate, distinct);
      tier.textContent = valid ? currentTier.label : '';
      selectedList.replaceChildren();

      selected.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.className = 'pro-refill-stack__selected-row';
        const label = document.createElement('span');
        label.textContent = `${item.productTitle} — ${item.title} × ${item.quantity}`;
        const bullet = document.createElement('span');
        bullet.textContent = '•';
        bullet.setAttribute('aria-hidden', 'true');
        const remove = document.createElement('button');
        remove.className = 'pro-refill-stack__remove';
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', root.dataset.removeTemplate.replace('__ITEM__', `${item.productTitle} ${item.title}`));
        remove.addEventListener('click', () => { selected.delete(item.id); render(); });
        listItem.append(remove, bullet, label);
        selectedList.append(listItem);
      });

      addStack.disabled = !valid || root.dataset.requiredReady !== 'true' || root.dataset.orderingEnabled !== 'true';
      status.textContent = selectionMessage(totals, root.dataset, targetQuantity);
    };

    root.querySelectorAll('[data-preset]').forEach((button) => {
      button.querySelector('[data-preset-saving]').textContent = goalLabel(Number(button.dataset.preset));
      button.addEventListener('click', () => {
        targetQuantity = Number(button.dataset.preset);
        customBox.dataset.selected = 'false';
        customQuantity.value = '';
        customQuantity.removeAttribute('aria-invalid');
        root.querySelector('[data-goal-error]').textContent = '';
        root.querySelectorAll('[data-preset]').forEach((preset) => preset.setAttribute('aria-pressed', String(preset === button)));
        render();
      });
    });

    customQuantity.addEventListener('input', () => {
      customBox.dataset.selected = 'true';
      root.querySelectorAll('[data-preset]').forEach(button => button.setAttribute('aria-pressed', 'false'));
      const quantity = Number(customQuantity.value);
      const valid = Number.isInteger(quantity) && quantity >= 6 && quantity <= 20;
      targetQuantity = valid ? quantity : null;
      customQuantity.setAttribute('aria-invalid', String(!valid && customQuantity.value !== ''));
      root.querySelector('[data-goal-error]').textContent = !valid && customQuantity.value !== '' ? root.dataset.customInvalid : '';
      render();
    });

    root.querySelectorAll('[data-bag-product]').forEach((productCard) => {
      const variantSelect = productCard.querySelector('[data-bag-variant]');
      variantSelect.replaceChildren(...[...variantSelect.options].sort((a, b) => compareSizeLabels(a.dataset.title, b.dataset.title)));
      variantSelect.selectedIndex = 0;
      const quantityInput = productCard.querySelector('[data-bag-quantity]');
      productCard.querySelector('[data-add-bag]').addEventListener('click', () => {
        const option = variantSelect.options[variantSelect.selectedIndex];
        if (!option || !option.value) return;
        const requestedQuantity = Number(quantityInput.value);
        const existing = selected.get(option.value);
        const quantity = quantityToAdd(requestedQuantity, getTotal(), existing?.quantity || 0, Number(option.dataset.stock));
        if (!quantity) {
          status.textContent = root.dataset.messageQuantity;
          return;
        }
        const nextQuantity = (existing ? existing.quantity : 0) + quantity;
        selected.set(option.value, {
          id: option.value,
          title: option.dataset.title,
          productTitle: productCard.querySelector('[data-product-title]').textContent,
          priceCents: Number(option.dataset.priceCents),
          quantity: nextQuantity
        });
        quantityInput.value = '1';
        render();
      });
    });

    addStack.addEventListener('click', async () => {
      const count = getTotal();
      const currentTier = getTier(count);
      if (addStack.disabled) return;
      addStack.disabled = true;
      status.textContent = root.dataset.messageSelect;
      const reportingProperties = {
        _pro_refill_stack: 'v1',
        _pro_refill_bag_count: String(count),
        _pro_refill_distinct_sizes: String(distinctSizes()),
        _pro_refill_tier_percent: String(currentTier.percentage)
      };
      const items = [
        { id: Number(root.dataset.extendVariantId), quantity: 1, properties: reportingProperties },
        { id: Number(root.dataset.prepVariantId), quantity: 1, properties: reportingProperties },
        ...[...selected.values()].map((item) => ({ id: Number(item.id), quantity: item.quantity, properties: reportingProperties }))
      ];

      try {
        const currentResponse = await fetch(root.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!currentResponse.ok) throw new Error('cart');
        const currentCart = await currentResponse.json();
        if (currentCart.items.some(item => item.properties?._pro_refill_stack === 'v1')) {
          status.textContent = root.dataset.messageExisting;
          return;
        }
        const addResponse = await fetch(root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!addResponse.ok) throw new Error('add');
        status.textContent = root.dataset.messageAdded;
        window.location.assign(root.dataset.cartUrl);
      } catch (error) {
        addStack.disabled = false;
        status.textContent = root.dataset.messageError;
      }
    });

    render();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-pro-refill-stack]').forEach(mountStack);
}
